/** A trigger character being typed in a draft: where it starts and what
 *  follows it up to the caret. Shared by the "/" slash-command popup and the
 *  "@" target picker — same open/close rule, different trigger character. */
export interface InputTrigger {
  start: number;
  query: string;
}

/** The last `char` before the caret, opening at the start of the draft or
 *  right after a space, with no whitespace (a newline included) between it
 *  and the caret. */
export function findInputTrigger(
  value: string,
  cursor: number,
  char: "/" | "@",
  chipOffset?: number | null,
): InputTrigger | null {
  const before = value.slice(0, cursor);
  const start = before.lastIndexOf(char);
  if (
    start === -1 ||
    (start > 0 && !/\s/.test(before[start - 1] ?? "") && start !== chipOffset)
  ) return null;
  const query = before.slice(start + 1);
  return /\s/.test(query) ? null : { start, query };
}

/** The trigger at a textarea's caret once `value` is committed to it, or at
 *  the end of the draft when there is no textarea. Reading the committed
 *  caret covers every draft change alike: typing, paste, dictation, prefill. */
export function findInputTriggerAtCaret(
  value: string,
  input: { selectionStart: number } | null,
  char: "/" | "@",
  chipOffset?: number | null,
): InputTrigger | null {
  return findInputTrigger(value, input?.selectionStart ?? value.length, char, chipOffset);
}

/** The draft without "@query"; unchanged when the token has since moved,
 *  changed, or grown into a longer word (a pick can resolve after the user
 *  typed on past the query it started from). Only a complete token — "@query"
 *  followed by whitespace or the end of the draft — is removed; a bare prefix
 *  match is left alone rather than stripped, so a slow pick can never corrupt
 *  a token the user has since edited. One space closes the gap left behind. */
export function removeInputTargetTrigger(
  value: string,
  { start, query }: InputTrigger,
): string {
  const token = `@${query}`;
  const after = value.slice(start + token.length);
  const isCompleteToken =
    value.startsWith(token, start) && (after === "" || /^\s/.test(after));
  if (!isCompleteToken) return value;
  const before = value.slice(0, start);
  const closesGap =
    (before === "" || before.endsWith(" ")) && after.startsWith(" ");
  return before + (closesGap ? after.slice(1) : after);
}
